// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — the chat composer's defense.local MARKING VOCABULARY + small CSS-FREE render
// helpers (no hooks, no CSS import — so they stay trivially node/tsx-testable).
//
// The classification ladder + releasability + caveat options the §7.9 [TDF] selector and
// the L2 room-create dropdown offer. Matches the vocabulary the rest of tak-tdf uses (the
// dod-marking banner builder + the live policy bundle seed: USA/GBR/CAN/AUS/NZL + the FVEY
// coalition; NOFORN/ORCON caveats). These are DISPLAY/INPUT choices only — the real gate is
// the room floor + the PDP over the sealed labels (the panel never decides clearance). Kept
// off the React tree so it is trivially unit-testable.
//
// INVARIANT (chatVocab.invariant.test.ts): every code offered here MUST be a member of the
// live defense.local `releasability` value set [USA,GBR,CAN,AUS,NZL,FVEY]. An offered code
// that is NOT in the bundle (the `NATO` drift, fixed here) makes EVERY seal throw
// ILLEGAL_RELEASABILITY in validateMarkings — no message ever seals.

/** The classification ladder, LOW → HIGH, matching cap-chat's CLASS_RANK. The composer
 *  offers these by name; the ROOM CLASS is the CEILING (L2 flip, 2026-07-06: a message
 *  content pick must be <= the room class — enforced in the capability as ROOM_CEILING,
 *  greyed here before the send round-trips). */
export const CLASSIFICATIONS = [
  "UNCLASSIFIED",
  "CUI",
  "CONFIDENTIAL",
  "SECRET",
  "TOP_SECRET",
] as const;

export type Classification = (typeof CLASSIFICATIONS)[number];

/** Rank within the ladder (mirrors cap-chat's classRank; unknown ⇒ +Infinity, fail-closed
 *  toward MORE restrictive so an unrecognized class is never offered as "below floor"). */
const RANK: Record<string, number> = Object.fromEntries(
  CLASSIFICATIONS.map((c, i) => [c, i]),
);
export function rankOf(classification: string): number {
  return RANK[(classification ?? "").toUpperCase()] ?? Number.POSITIVE_INFINITY;
}

/** The releasability codes (trigraphs + coalition tetragraphs) the dropdown offers,
 *  matching the live bundle seed + the dod-marking REL-TO ordering. */
export const RELEASABILITIES = [
  "USA",
  "GBR",
  "CAN",
  "AUS",
  "NZL",
  "FVEY",
] as const;

/** Spelled-out, full-caps label for a classification (TOP_SECRET → "TOP SECRET"). */
export function classLabel(classification: string): string {
  return (classification ?? "").replace(/_/g, " ");
}

/** Is a message content pick WITHIN the room CEILING (at-or-below the room class)?
 *  DISPLAY-side guard for the composer (the capability's assertRoomMarking is the
 *  AUTHORITATIVE gate — ROOM_CEILING; this just lets the UI grey an invalid choice before
 *  the send round-trips). Replaces the pre-flip `meetsFloor` (markings could be raised
 *  above the room floor, never lowered — 2026-07-06 inverted the semantic). Fail-closed:
 *  an UNKNOWN pick ranks +Infinity, so it is NEVER offered as within the ceiling. */
export function withinCeiling(marking: string, roomClass: string): boolean {
  return rankOf(marking) <= rankOf(roomClass);
}

// ---------------------------------------------------------------------------
// @MENTION PARSING + CSS-FREE RENDER HELPERS (UX-only, no crypto)
// ---------------------------------------------------------------------------
//
// A client-side @mention is a PURE TEXT layer over the already-decoded message body
// (ChatMessage.body): we split the cleartext on `@handle` tokens and wrap each mention in
// its own styled <span class="chat__mention">. It changes NOTHING about the sealed
// envelope — a redacted (shell-only) message never reaches here (its body is never shown).
// Kept off the React tree (plain `createElement`, no hooks, no CSS import) so the exact
// render the panel uses is renderToStaticMarkup-testable under tsx.

import { createElement, type ReactNode } from "react";

/** A handle token: `@` + a letter/digit start, optional inner word chars / `.` `_` `-`, and
 *  an ALPHANUMERIC end. Matches callsign/uid shapes ("first.last", "tf-bravo") while leaving
 *  trailing punctuation (`,` `.` `!`) OUTSIDE the token — "thanks @bob." highlights "@bob". */
const MENTION_RE = /@[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?/g;

/** One segment of a parsed body: plain text, or a mention carrying its bare handle. */
export interface BodySegment {
  /** The literal text of this segment (includes the leading `@` for a mention). */
  text: string;
  /** True when this segment is an `@handle` mention token. */
  mention: boolean;
  /** The bare handle (no leading `@`) when `mention` is true. */
  handle?: string;
}

/** Split a cleartext body into ordered plain/mention segments. PURE — the single source of
 *  truth for "what is a mention", shared by the panel render and its test. */
export function mentionSegments(body: string): BodySegment[] {
  const out: BodySegment[] = [];
  if (!body) return out;
  let last = 0;
  for (const m of body.matchAll(MENTION_RE)) {
    const start = m.index ?? 0;
    if (start > last) out.push({ text: body.slice(last, start), mention: false });
    const token = m[0];
    out.push({ text: token, mention: true, handle: token.slice(1) });
    last = start + token.length;
  }
  if (last < body.length) out.push({ text: body.slice(last), mention: false });
  return out;
}

/** Render a cleartext body as React nodes, wrapping each `@handle` mention in its own
 *  <span class="chat__mention">. A mention targeting the acting subject gets the extra
 *  `chat__mention--you` class (the "you were mentioned" emphasis); a handle that resolves
 *  to NO known roster member gets `chat__mention--unresolved` (still highlighted, but muted)
 *  so a typo'd mention reads differently from a real one. MONO-safe: the class never relies
 *  on hue alone (see chat.css — weight + background + underline). */
export function renderMessageBody(
  body: string,
  opts?: { self?: string; isKnownHandle?: (handle: string) => boolean },
): ReactNode {
  const segs = mentionSegments(body);
  // No mentions → return the bare string (cheapest, and keeps the DOM identical to before).
  if (!segs.some((s) => s.mention)) return body;
  return segs.map((s, i) => {
    if (!s.mention) return s.text;
    const handle = s.handle ?? "";
    const isSelf = opts?.self ? handle.toLowerCase() === opts.self.toLowerCase() : false;
    const known = opts?.isKnownHandle ? opts.isKnownHandle(handle) : true;
    const cls =
      "chat__mention" +
      (isSelf ? " chat__mention--you" : "") +
      (known ? "" : " chat__mention--unresolved");
    return createElement("span", { key: i, className: cls, "data-mention": handle }, s.text);
  });
}

/** Render a numeric UNREAD BADGE as a <span class="chat__unread">, or `null` when the count
 *  is 0/negative (no badge — the caller renders nothing). Caps the display at "99+". Shared
 *  by the channel-row badges and (via the same class family) the rail global badge. */
export function renderUnreadBadge(
  count: number,
  opts?: { className?: string; label?: string },
): ReactNode {
  if (!Number.isFinite(count) || count <= 0) return null;
  const text = count > 99 ? "99+" : String(count);
  return createElement(
    "span",
    {
      className: opts?.className ?? "chat__unread",
      "aria-label": opts?.label ?? `${count} unread`,
    },
    text,
  );
}
